import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Dumbbell,
  Building2,
  FlaskConical,
  Pill,
  Salad,
  User,
  Mail,
  Lock,
  Phone,
  Globe,
  MapPin,
  Eye,
  EyeOff,
  ShieldCheck,
  CheckCircle,
  ArrowLeft,
  BadgeCheck,
  Sparkles,
} from 'lucide-react';

const centerTypeOptions = [
  { value: 'WELLNESS_CENTER', label: 'Centru Wellness & Recovery', icon: Building2, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  { value: 'CROSSFIT_HIIT', label: 'Centru CrossFit & HIIT', icon: Dumbbell, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  { value: 'NUTRITION_STUDIO', label: 'Studio Nutriție Sportivă', icon: Salad, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
  { value: 'FITNESS_CENTER', label: 'Sală de Sport / Centru Fitness', icon: Dumbbell, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  { value: 'SUPPLEMENTS_SHOP', label: 'Magazin Suplimente Sportive', icon: Pill, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  { value: 'YOGA_PILATES_STUDIO', label: 'Studio Yoga / Pilates', icon: FlaskConical, color: 'text-cyan-600', bg: 'bg-cyan-50', border: 'border-cyan-200' },
];

const planOptions = [
  {
    value: 'BASIC',
    name: 'Basic',
    price: '149 lei/lună',
    desc: 'Profil + 3 antrenori + hartă',
    color: 'border-gray-300',
    selectedColor: 'border-gray-800 bg-gray-50',
    badge: '',
  },
  {
    value: 'PRO',
    name: 'Pro',
    price: '349 lei/lună',
    desc: 'Programări + postări + statistici',
    color: 'border-teal-300',
    selectedColor: 'border-teal-600 bg-teal-50',
    badge: 'Cel mai popular',
  },
  {
    value: 'PREMIUM',
    name: 'Premium',
    price: '699 lei/lună',
    desc: 'AI targeting + banner + CRM',
    color: 'border-indigo-300',
    selectedColor: 'border-indigo-600 bg-indigo-50',
    badge: '',
  },
];

const romanianCounties = [
  'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani',
  'Brăila', 'Brașov', 'București', 'Buzău', 'Călărași', 'Caraș-Severin',
  'Cluj', 'Constanța', 'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu',
  'Gorj', 'Harghita', 'Hunedoara', 'Ialomița', 'Iași', 'Ilfov', 'Maramureș',
  'Mehedinți', 'Mureș', 'Neamț', 'Olt', 'Prahova', 'Sălaj', 'Satu Mare',
  'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea', 'Vâlcea', 'Vaslui', 'Vrancea',
];

export default function BusinessRegisterPage() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/specialist/dashboard');
    }
  }, [user, navigate]);

  // Pre-select plan from URL param (e.g. /business/register?plan=PRO)
  const planFromUrl = searchParams.get('plan')?.toUpperCase() || 'PRO';

  // Step state
  const [step, setStep] = useState(1); // 1 = center type, 2 = details, 3 = account

  // Step 1 — Center type
  const [centerType, setCenterType] = useState('');

  // Step 2 — Center details
  const [centerName, setCenterName] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [county, setCounty] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(planFromUrl);

  // Step 3 — Account
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  // UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Password strength
  useEffect(() => {
    if (!password) { setPasswordStrength(0); return; }
    let s = 0;
    if (password.length >= 8) s++;
    if (/[a-z]/.test(password)) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[\d\W]/.test(password)) s++;
    setPasswordStrength(s);
  }, [password]);

  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500'];
  const strengthLabels = ['Foarte slabă', 'Slabă', 'Medie', 'Puternică'];

  const handleStep1 = () => {
    if (!centerType) { setError('Selectează tipul centrului.'); return; }
    setError('');
    setStep(2);
  };

  const handleStep2 = () => {
    if (!centerName.trim() || !phone.trim() || !county) {
      setError('Completează toate câmpurile obligatorii.');
      return;
    }
    setError('');
    setStep(3);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!acceptTerms) { setError('Trebuie să accepți termenii și condițiile.'); return; }
    if (password !== confirmPassword) { setError('Parolele nu se potrivesc.'); return; }
    if (password.length < 8) { setError('Parola trebuie să aibă cel puțin 8 caractere.'); return; }
    if (!firstName.trim() || !lastName.trim()) { setError('Completează numele persoanei de contact.'); return; }

    setLoading(true);

    try {
      const sanitizedEmail = email.trim().toLowerCase();

      // Build description with business metadata so it's stored in the specialist profile
      const businessMeta = `[BUSINESS] Tip: ${centerType} | Centru: ${centerName} | Plan: ${selectedPlan} | Județ: ${county}${website ? ` | Web: ${website}` : ''}`;

      await register(
        sanitizedEmail,
        password.trim(),
        firstName.trim(),
        lastName.trim(),
        'SPECIALIST',
        // Pass extra context via description field if supported, otherwise just register
      );

      // After registration, redirect to specialist profile edit to complete the business profile
      navigate('/specialist/profile/edit', {
        state: {
          fromBusiness: true,
          centerType,
          centerName,
          phone,
          website,
          county,
          selectedPlan,
          businessMeta,
        },
      });
    } catch (err: any) {
      setError(err.message || 'Eroare la înregistrare. Încearcă din nou.');
    } finally {
      setLoading(false);
    }
  };

  const selectedCenterType = centerTypeOptions.find((c) => c.value === centerType);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-slate-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/business" className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-teal-600 rounded-xl flex items-center justify-center shadow">
                <Dumbbell className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-lg font-extrabold text-gray-900">FII FIT</span>
                <span className="text-[10px] font-bold text-teal-600 uppercase tracking-widest -mt-0.5">Business</span>
              </div>
            </Link>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>Ai deja cont?</span>
              <Link to="/login" className="text-teal-600 font-semibold hover:text-teal-700">
                Conectare
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-700 px-4 py-1.5 rounded-full text-xs font-bold mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            30 de zile gratuit — Fără card
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
            Înregistrează centrul tău de fitness
          </h1>
          <p className="text-gray-500">
            Completează în 3 pași simpli și fii vizibil pentru mii de pasionați de fitness.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-3 mb-10">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-3">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step > s
                    ? 'bg-teal-600 text-white'
                    : step === s
                    ? 'bg-teal-600 text-white ring-4 ring-teal-100'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              <span className={`text-sm font-medium hidden sm:block ${step === s ? 'text-teal-700' : 'text-gray-400'}`}>
                {s === 1 ? 'Tip centru' : s === 2 ? 'Detalii centru' : 'Cont & Finalizare'}
              </span>
              {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-teal-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* ── STEP 1: Tip centru ─────────────────────────────────────── */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Ce tip de centru reprezinți?</h2>
            <p className="text-sm text-gray-500 mb-6">Selectează categoria care descrie cel mai bine activitatea ta.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {centerTypeOptions.map((ct) => (
                <button
                  key={ct.value}
                  type="button"
                  onClick={() => { setCenterType(ct.value); setError(''); }}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                    centerType === ct.value
                      ? `${ct.border} ${ct.bg}`
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg ${ct.bg} flex items-center justify-center flex-shrink-0`}>
                    <ct.icon className={`w-5 h-5 ${ct.color}`} />
                  </div>
                  <span className="text-sm font-semibold text-gray-800">{ct.label}</span>
                  {centerType === ct.value && (
                    <CheckCircle className={`w-5 h-5 ${ct.color} ml-auto flex-shrink-0`} />
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={handleStep1}
              className="w-full bg-teal-600 text-white py-3.5 rounded-xl font-bold hover:bg-teal-700 transition-all shadow-md"
            >
              Continuă →
            </button>

            <p className="text-center text-sm text-gray-400 mt-4">
              Ești client?{' '}
              <Link to="/register" className="text-teal-600 font-semibold hover:underline">
                Creează cont de client
              </Link>
            </p>
          </div>
        )}

        {/* ── STEP 2: Detalii centru ─────────────────────────────────── */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Înapoi
            </button>

            <h2 className="text-xl font-bold text-gray-900 mb-2">Detalii despre centrul tău</h2>
            <p className="text-sm text-gray-500 mb-6">
              Aceste informații vor apărea pe profilul tău public în FII FIT.
            </p>

            <div className="space-y-5">
              {/* Center name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Building2 className="w-4 h-4 inline mr-1.5" />
                  Numele centrului *
                </label>
                <input
                  type="text"
                  value={centerName}
                  onChange={(e) => setCenterName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="ex: FitZone Cluj, PowerGym București"

                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1.5" />
                  Telefon *
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="+40 123 456 789"
                />
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Globe className="w-4 h-4 inline mr-1.5" />
                  Website (opțional)
                </label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="https://centrul-tau.ro"
                />
              </div>

              {/* County */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1.5" />
                  Județ *
                </label>
                <select
                  value={county}
                  onChange={(e) => setCounty(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">Selectează județul...</option>
                  {romanianCounties.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Plan selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Alege pachetul *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {planOptions.map((plan) => (
                    <button
                      key={plan.value}
                      type="button"
                      onClick={() => setSelectedPlan(plan.value)}
                      className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                        selectedPlan === plan.value ? plan.selectedColor : plan.color + ' bg-white hover:bg-gray-50'
                      }`}
                    >
                      {plan.badge && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-teal-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                          {plan.badge}
                        </span>
                      )}
                      <p className="font-bold text-gray-900 text-sm mt-1">{plan.name}</p>
                      <p className="text-teal-700 font-semibold text-xs mt-0.5">{plan.price}</p>
                      <p className="text-gray-500 text-[11px] mt-1 leading-tight">{plan.desc}</p>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Poți schimba planul oricând. 30 de zile gratuit.
                </p>
              </div>
            </div>

            <button
              onClick={handleStep2}
              className="w-full mt-8 bg-teal-600 text-white py-3.5 rounded-xl font-bold hover:bg-teal-700 transition-all shadow-md"
            >
              Continuă →
            </button>
          </div>
        )}

        {/* ── STEP 3: Cont ───────────────────────────────────────────── */}
        {step === 3 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <button
              onClick={() => setStep(2)}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Înapoi
            </button>

            <h2 className="text-xl font-bold text-gray-900 mb-2">Creează contul tău</h2>
            <p className="text-sm text-gray-500 mb-6">
              Datele persoanei de contact responsabile pentru acest centru.
            </p>

            {/* Summary card */}
            {selectedCenterType && (
              <div className={`flex items-center gap-3 p-4 rounded-xl ${selectedCenterType.bg} border ${selectedCenterType.border} mb-6`}>
                <selectedCenterType.icon className={`w-5 h-5 ${selectedCenterType.color} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">{centerName}</p>
                  <p className="text-xs text-gray-500">{selectedCenterType.label} · {county}</p>
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-full bg-white border ${selectedCenterType.border} ${selectedCenterType.color}`}>
                  {planOptions.find((p) => p.value === selectedPlan)?.name}
                </span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-1.5" />
                    Prenume *
                  </label>
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Ion"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nume *
                  </label>
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Popescu"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-1.5" />
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="contact@centrul-tau.ro"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Lock className="w-4 h-4 inline mr-1.5" />
                  Parolă *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Minim 8 caractere"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {password && (
                  <div className="mt-2">
                    <div className="flex gap-1">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded ${i < passwordStrength ? strengthColors[passwordStrength - 1] : 'bg-gray-200'}`}
                        />
                      ))}
                    </div>
                    <p className="text-xs mt-1 text-gray-500">
                      {passwordStrength > 0 ? strengthLabels[passwordStrength - 1] : ''}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <ShieldCheck className="w-4 h-4 inline mr-1.5" />
                  Confirmă parola *
                </label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    confirmPassword && password !== confirmPassword
                      ? 'border-red-400'
                      : confirmPassword && password === confirmPassword
                      ? 'border-green-400'
                      : 'border-gray-300'
                  }`}
                  placeholder="Repetă parola"
                />
              </div>

              {/* Terms */}
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <input
                    id="terms"
                    type="checkbox"
                    required
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded mt-0.5"
                  />
                  <label htmlFor="terms" className="text-sm text-gray-700">
                    Sunt de acord cu{' '}
                    <Link to="/terms" target="_blank" className="text-teal-600 font-semibold hover:underline">
                      Termenii și Condițiile
                    </Link>{' '}
                    și{' '}
                    <Link to="/privacy" target="_blank" className="text-teal-600 font-semibold hover:underline">
                      Politica de Confidențialitate
                    </Link>{' '}
                    FII FIT Business. *
                  </label>
                </div>

                <div className="p-3 bg-teal-50 border border-teal-200 rounded-xl">
                  <p className="text-xs text-teal-800">
                    <BadgeCheck className="w-3.5 h-3.5 inline mr-1" />
                    <strong>30 de zile gratuit</strong> — Fără card necesar. Poți anula oricând.
                    Abonamentul se activează după perioada de probă.
                  </p>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-600 text-white py-4 rounded-xl font-bold hover:bg-teal-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-lg"
              >
                {loading ? 'Se creează contul...' : '🚀 Creează contul și începe gratuit'}
              </button>
            </form>
          </div>
        )}

        {/* Security notice */}
        <div className="flex items-center justify-center gap-6 mt-8 text-xs text-gray-400">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-teal-500" />
            SSL Securizat
          </div>
          <div className="flex items-center gap-1.5">
            <Lock className="w-4 h-4 text-teal-500" />
            Date Criptate
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-teal-500" />
            GDPR Compliant
          </div>
        </div>
      </div>
    </div>
  );
}
